import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, arrayMove, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// "Analyze as a series" — docs/design/series-analysis-design-v0-5.md.
// Plain words on purpose: one word for one thing ("episode", "series report", "credit").

const TERMINAL = ['done', 'done_without_report', 'cancelled', 'failed'];
const STATUS_TEXT = {
    extracting: 'Reading each episode',
    contexts: 'Connecting the episodes',
    analyzing: 'Analyzing each episode',
    reporting: 'Writing the series report',
    done: 'Done',
    done_without_report: 'Done — the series report could not be written (its credit was refunded)',
    cancel_requested: 'Cancelling',
    draining: 'Cancelling — waiting for analyses already running to finish',
    cancelled: 'Cancelled',
    failed: 'Stopped',
};
const EP_TEXT = { pending: 'Waiting', running: 'Analyzing', done: 'Done', failed: 'Could not start (credit refunded)', uncertain: 'Checking — we will sort this out' };

function naturalCompare(a, b) {
    return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' });
}

// One row of the episode order: drag by the handle (mouse, touch, or keyboard: focus the handle, Space, arrows, Space).
function EpisodeRow({ id, index, total, title, included, onToggle, onMove, number }) {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
    const style = { transform: CSS.Transform.toString(transform), transition };
    return (
        <li ref={setNodeRef} style={style}
            className={`list-group-item d-flex align-items-center series-episode ${isDragging ? 'dragging' : ''} ${included ? '' : 'text-muted'}`}>
            <span className="drag-handle" {...attributes} {...listeners} aria-label={`Drag to reorder ${title}`} title="Drag to reorder">⠿</span>
            <span className="episode-number">{included ? `${number}.` : ''}</span>
            <input type="checkbox" className="me-2" checked={included} onChange={e => onToggle(e.target.checked)} aria-label={`Include ${title}`} />
            <span className="flex-grow-1">{title}</span>
            <button className="btn btn-sm btn-light" aria-label="Move up" onClick={() => onMove(-1)} disabled={index === 0}>▲</button>
            <button className="btn btn-sm btn-light ms-1" aria-label="Move down" onClick={() => onMove(1)} disabled={index === total - 1}>▼</button>
        </li>
    );
}

function Question({ label, help, value, onChange, name }) {
    return (
        <div className="mb-2">
            <div className="fw-semibold">{label}</div>
            {help && <div className="small text-muted mb-1">{help}</div>}
            {[['yes', 'Yes'], ['no', 'No'], ['unknown', 'Not sure']].map(([v, t]) => (
                <label key={v} className="me-3">
                    <input type="radio" name={name} value={v} checked={value === v} onChange={() => onChange(v)} /> {t}
                </label>
            ))}
        </div>
    );
}

export function SeriesPanel({ batchUuid, movies, user, initialRun, onCreditsChange, showError, showSuccess }) {
    const [run, setRun] = useState(initialRun || null);
    const [open, setOpen] = useState(false);
    const [order, setOrder] = useState([]);
    const [included, setIncluded] = useState({});
    const [edition, setEdition] = useState('standard');
    const [coverage, setCoverage] = useState('unknown');
    const [pilot, setPilot] = useState('unknown');
    const [endpoint, setEndpoint] = useState('unknown');
    const [busy, setBusy] = useState(false);
    // One key per opened form: a retried or double-clicked start is the same start (server dedupes on it).
    const [requestKey, setRequestKey] = useState(() => (window.crypto?.randomUUID ? window.crypto.randomUUID() : String(Date.now()) + Math.random()));

    useEffect(() => {
        const ids = [...movies].sort((a, b) => naturalCompare(a.title || a.original_filename, b.title || b.original_filename)).map(m => m.id);
        setOrder(prev => [...prev.filter(id => ids.includes(id)), ...ids.filter(id => !prev.includes(id))]);
        setIncluded(prev => Object.fromEntries(ids.map(id => [id, prev[id] ?? true])));
    }, [movies]);

    useEffect(() => {
        if (!run || TERMINAL.includes(run.status)) return;
        const t = setInterval(async () => {
            try { const r = await axios.get(`/series/${run.uuid}`); setRun(r.data.run); } catch (e) { /* keep last state */ }
        }, 15000);
        return () => clearInterval(t);
    }, [run?.uuid, run?.status]);

    const byId = useMemo(() => Object.fromEntries(movies.map(m => [m.id, m])), [movies]);
    const chosen = order.filter(id => included[id]);
    const cost = chosen.length + 1;
    const balance = edition === 'deluxe' ? (user?.movie_credits_deluxe ?? 0) : (user?.movie_credits ?? 0);
    const move = (i, d) => setOrder(o => { const n = [...o]; const j = i + d; if (j < 0 || j >= n.length) return o; [n[i], n[j]] = [n[j], n[i]]; return n; });
    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
    );
    const onDragEnd = ({ active, over }) => {
        if (!over || active.id === over.id) return;
        setOrder(o => arrayMove(o, o.indexOf(active.id), o.indexOf(over.id)));
    };
    const numberOf = (id) => chosen.indexOf(id) + 1;   // episode number among the included scripts

    const start = async () => {
        setBusy(true);
        try {
            const r = await axios.post(`/batches/${batchUuid}/series`, {
                movie_ids: chosen, edition, coverage, starts_with_pilot: pilot, endpoint, request_key: requestKey,
            });
            setRun(r.data.run);
            setOpen(false);
            onCreditsChange && onCreditsChange(edition, cost);
            showSuccess && showSuccess('Series analysis started.');
        } catch (e) {
            showError && showError(e.response?.data?.error || 'Could not start the series analysis.');
        } finally { setBusy(false); }
    };

    const cancel = async () => {
        if (!run) return;
        try { const r = await axios.post(`/series/${run.uuid}/cancel`); setRun(r.data.run); } catch (e) { showError && showError('Could not cancel.'); }
    };

    if (run && !TERMINAL.includes(run.status)) {
        return (
            <div className="card mb-3 border-primary">
                <div className="card-body">
                    <h5 className="card-title">Series analysis: {STATUS_TEXT[run.status] || run.status}</h5>
                    <ol className="mb-2">
                        {run.episodes.map(e => (
                            <li key={e.position}>{e.title} — {EP_TEXT[e.analysis] || e.analysis}</li>
                        ))}
                    </ol>
                    <div className="small text-muted mb-2">These scripts are locked until the series analysis finishes.</div>
                    {!['cancel_requested', 'draining'].includes(run.status) && (
                        <button className="btn btn-outline-secondary btn-sm" onClick={cancel}>Cancel series analysis</button>
                    )}
                </div>
            </div>
        );
    }

    return (
        <div className="card mb-3">
            <div className="card-body">
                {run && run.status === 'done' && run.has_report && (
                    <div className="alert alert-success py-2">
                        Your series report is ready. <a href={`/series/${run.uuid}/report`}>Open the series report</a>
                    </div>
                )}
                {run && run.status === 'done_without_report' && (
                    <div className="alert alert-warning py-2">{STATUS_TEXT.done_without_report}. Each episode's analysis is ready below.</div>
                )}
                {!open ? (
                    <div className="d-flex justify-content-between align-items-center">
                        <div>
                            <strong>Episodes of a TV series?</strong> Analyze them together: each episode's analysis uses what
                            happened in the episodes before it, and you get a series report across all of them.
                        </div>
                        <button className="btn btn-primary btn-sm ms-3" disabled={movies.length < 2} onClick={() => setOpen(true)}>Analyze as a series</button>
                    </div>
                ) : (
                    <div>
                        <h5>Analyze as a series</h5>
                        <div className="mb-3">
                            <div className="fw-semibold">1. Put the episodes in order</div>
                            <div className="small text-muted mb-1">Drag the episodes into order (or use the arrows). Untick any script that is not part of this series.</div>
                            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
                                <SortableContext items={order} strategy={verticalListSortingStrategy}>
                                    <ol className="list-group">
                                        {order.map((id, i) => (
                                            <EpisodeRow key={id} id={id} index={i} total={order.length}
                                                title={byId[id]?.title || byId[id]?.original_filename}
                                                included={!!included[id]} number={numberOf(id)}
                                                onToggle={v => setIncluded(s => ({ ...s, [id]: v }))}
                                                onMove={d => move(i, d)} />
                                        ))}
                                    </ol>
                                </SortableContext>
                            </DndContext>
                        </div>
                        <div className="mb-3">
                            <div className="fw-semibold mb-1">2. A few questions (optional)</div>
                            <Question name="pilot" label="Is the first episode the pilot?" value={pilot} onChange={setPilot} />
                            <Question name="coverage" label="Did you include every episode up to the last one here?"
                                help="If some are missing, we won't treat something as unexplained just because it happened in an episode we didn't see."
                                value={coverage} onChange={setCoverage} />
                            <Question name="endpoint" label="Is the last episode a season or series finale?"
                                help="Only then do we look at whether storylines that were due got resolved."
                                value={endpoint} onChange={setEndpoint} />
                        </div>
                        <div className="mb-3">
                            <div className="fw-semibold mb-1">3. Analysis type</div>
                            <select className="form-select form-select-sm w-auto" value={edition} onChange={e => setEdition(e.target.value)}>
                                <option value="standard">Standard ({user?.movie_credits ?? 0} credits)</option>
                                <option value="deluxe">Deep Focus ({user?.movie_credits_deluxe ?? 0} credits)</option>
                            </select>
                        </div>
                        <div className="mb-2">
                            This uses <strong>{cost} credits</strong>: one per episode ({chosen.length}) plus one for the series report.
                            {balance < cost && <span className="text-danger"> You have {balance}.</span>}
                        </div>
                        <button className="btn btn-primary" disabled={busy || chosen.length < 2 || chosen.length > 10 || balance < cost} onClick={start}>
                            {busy ? 'Starting…' : 'Analyze series'}
                        </button>
                        <button className="btn btn-link" onClick={() => setOpen(false)}>Not now</button>
                        {chosen.length > 10 && <div className="small text-danger">For now a series can have at most 10 episodes.</div>}
                    </div>
                )}
            </div>
        </div>
    );
}
